<template>
  <v-card class="glass-card mt-2">
    <!-- Gradient header -->
    <div class="gradient-header pa-4 d-flex align-center">
      <v-icon color="white" class="mr-3">mdi-tune</v-icon>
      <span class="white--text text-h6 font-weight-medium">Nov ispravak normativa</span>
    </div>

    <v-card-text class="pa-4">
      <v-stepper v-model="step" vertical flat class="glass-stepper">
        <!-- Step 1 -->
        <v-stepper-step :complete="step > 1" step="1" color="primary">
          Odabir sastojka i perioda
          <small>Odaberite sastojak, period i delta vrijednost</small>
        </v-stepper-step>
        <v-stepper-content step="1">
          <v-row class="mt-1">
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="selectedIngredient"
                :items="ingredients"
                :loading="loadingIngredients"
                item-text="name"
                item-value="id"
                label="Sastojak"
                outlined
                dense
                return-object
                clearable
                prepend-inner-icon="mdi-package-variant"
                class="glass-input"
                @focus="loadIngredients"
              />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field
                v-model="dateFrom"
                label="Datum od"
                type="date"
                outlined
                dense
                class="glass-input"
              />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field
                v-model="dateTo"
                label="Datum do"
                type="date"
                outlined
                dense
                class="glass-input"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="delta"
                label="Delta količina"
                type="number"
                outlined
                dense
                hint="Pozitivno = dodaj, negativno = oduzmi"
                persistent-hint
                class="glass-input"
                :suffix="selectedIngredient ? selectedIngredient.unit : ''"
              />
            </v-col>
          </v-row>
          <v-btn
            class="gradient-btn mt-3"
            :disabled="!canProceedStep1"
            :loading="loadingStep2"
            elevation="0"
            @click="loadShipmentData"
          >
            <v-icon left small>mdi-magnify</v-icon>
            Učitaj artikle
          </v-btn>
        </v-stepper-content>

        <!-- Step 2 -->
        <v-stepper-step :complete="step > 2" step="2" color="primary">
          Distribucija ispravka
          <small>Rasporedite delta po artiklima</small>
        </v-stepper-step>
        <v-stepper-content step="2">
          <v-row align="center" class="mb-3">
            <v-col cols="auto">
              <v-btn-toggle v-model="distributionMode" mandatory dense rounded class="glass-toggle">
                <v-btn value="proportional" small>
                  <v-icon left x-small>mdi-chart-pie</v-icon>
                  Po artiklu
                </v-btn>
                <v-btn value="manual" small>
                  <v-icon left x-small>mdi-pencil</v-icon>
                  Manualno
                </v-btn>
              </v-btn-toggle>
            </v-col>
            <v-col cols="auto">
              <div class="totals-chip" :class="isDistributionBalanced ? 'totals-ok' : 'totals-warn'">
                <v-icon small class="mr-1">{{ isDistributionBalanced ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
                Distribuirano: <strong>{{ totalDistributed.toFixed(3) }}</strong> / {{ Math.abs(delta) }} kg
                <span class="ml-3">
                  Računi: <strong>{{ totalAllocatedReceipts }}</strong> / {{ totalReceipts }}
                </span>
                <span v-if="!isDistributionBalanced" class="ml-2">
                  (preostalo: <strong>{{ remainingToDistribute.toFixed(3) }}</strong>)
                </span>
              </div>
            </v-col>
            <v-spacer />
            <v-col cols="auto">
              <v-btn x-small outlined @click="clearAllAllocations">
                <v-icon left x-small>mdi-eraser</v-icon>
                Očisti sve
              </v-btn>
            </v-col>
          </v-row>

          <v-data-table
            :headers="distributionHeaders"
            :items="visibleProductRows"
            :items-per-page="-1"
            hide-default-footer
            dense
            class="glass-table"
          >
            <template v-slot:item.receiptRatio="{ item }">
              <span :class="(item.allocatedReceipts && item.allocatedReceipts.length > 0) ? 'success--text' : ''">
                {{ item.allocatedReceipts ? item.allocatedReceipts.length : 0 }}
              </span>
              / {{ item.receipts ? item.receipts.length : 0 }}
            </template>
            <template v-slot:item.totalQty="{ item }">
              {{ formatNum(item.totalQty / 1000) }}
            </template>
            <template v-slot:item.ingredientTotal="{ item }">
              {{ formatNum(item.ingredientTotal) }} {{ selectedIngredient ? selectedIngredient.unit : '' }}
            </template>
            <template v-slot:item.allocated="{ item }">
              <v-text-field
                v-if="distributionMode === 'manual'"
                v-model.number="item.allocated"
                type="number"
                dense
                outlined
                hide-details
                class="glass-input"
                style="max-width: 120px;"
              />
              <strong v-else>{{ formatNum(item.allocated) }}</strong>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn v-if="item.excluded" icon x-small color="success" @click="includeRow(item)">
                <v-icon small>mdi-plus</v-icon>
              </v-btn>
              <v-btn v-else icon x-small @click="clearRowAllocation(item)">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </template>
          </v-data-table>

          <v-row v-if="distributionMode === 'manual'" class="mt-3" align="center">
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="manualProduct"
                :items="manualProductOptions"
                :loading="loadingManualSearch"
                :search-input.sync="manualSearch"
                item-text="name"
                item-value="id"
                label="Dodaj artikl ručno"
                outlined
                dense
                clearable
                return-object
                hide-no-data
                prepend-inner-icon="mdi-plus"
                class="glass-input"
                @change="addManualProduct"
              />
            </v-col>
          </v-row>

          <div class="mt-2">
            <v-btn text class="mr-2" @click="step = 1">
              <v-icon left small>mdi-arrow-left</v-icon>Nazad
            </v-btn>
            <v-btn
              class="gradient-btn"
              elevation="0"
              :disabled="!isDistributionBalanced || productRows.length === 0"
              @click="step = 3"
            >
              Nastavi
              <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
          </div>
        </v-stepper-content>

        <!-- Step 3 -->
        <v-stepper-step step="3" color="primary">
          Potvrda i slanje
          <small>Pregledajte ispravak i pošaljite</small>
        </v-stepper-step>
        <v-stepper-content step="3">
          <div class="summary-box mb-4">
            <div class="summary-row">
              <span class="summary-label">Artikl</span>
              <span class="summary-value">{{ selectedIngredient ? selectedIngredient.name : '—' }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Period</span>
              <span class="summary-value">{{ dateFrom }} – {{ dateTo }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Delta</span>
              <span class="summary-value" :class="delta >= 0 ? 'success--text' : 'error--text'">
                {{ delta >= 0 ? '+' : '' }}{{ delta }} {{ selectedIngredient ? selectedIngredient.unit : '' }}
              </span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Broj artikala</span>
              <span class="summary-value">{{ productRows.length }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Broj stavki</span>
              <span class="summary-value">{{ totalShipmentNotes }}</span>
            </div>
          </div>

          <div v-if="jobId" class="mb-4">
            <div class="progress-label mb-1 caption grey--text">{{ jobStatus }}</div>
            <v-progress-linear
              :value="jobProgress"
              height="8"
              rounded
              class="gradient-progress"
            />
            <v-alert v-if="jobError" type="error" dense outlined class="mt-2">{{ jobError }}</v-alert>
            <v-alert v-if="jobDone" type="success" dense outlined class="mt-2">
              <v-icon left>mdi-check-circle</v-icon>Ispravak uspješno kreiran!
            </v-alert>
          </div>

          <div class="mt-2">
            <v-btn text class="mr-2" :disabled="!!jobId" @click="step = 2">
              <v-icon left small>mdi-arrow-left</v-icon>Nazad
            </v-btn>
            <v-btn
              class="gradient-btn"
              elevation="0"
              :loading="submitting"
              :disabled="!!jobId"
              @click="submitCorrection"
            >
              <v-icon left small>mdi-check</v-icon>
              Potvrdi ispravak
            </v-btn>
            <v-btn
              v-if="jobDone"
              text
              class="ml-2"
              @click="resetWizard"
            >
              <v-icon left small>mdi-plus</v-icon>Nov ispravak
            </v-btn>
          </div>
        </v-stepper-content>
      </v-stepper>
    </v-card-text>
  </v-card>
</template>

<script>
import { df } from '@/plugins/firebase'
import axios from 'axios'
import state from '@/state'

export default {
  name: 'NovIspravak',
  props: {
    warehouseId: { type: String, required: true },
    locationId: { type: String, required: true },
    companyId: { type: String, required: true }
  },
  data () {
    return {
      step: 1,
      ingredients: [],
      loadingIngredients: false,
      selectedIngredient: null,
      dateFrom: (() => {
        const d = new Date()
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
      })(),
      dateTo: (() => {
        const d = new Date()
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      })(),
      delta: 0,
      loadingStep2: false,
      distributionMode: 'proportional',
      productRows: [],
      manualProduct: null,
      manualSearch: '',
      manualProductOptions: [],
      loadingManualSearch: false,
      soldProducts: [], // svi artikli prodani u periodu
      submitting: false,
      jobId: null,
      jobStatus: '',
      jobProgress: 0,
      jobDone: false,
      jobError: null,
      pollInterval: null
    }
  },
  computed: {
    canProceedStep1 () {
      return this.selectedIngredient && this.dateFrom && this.dateTo && this.delta !== 0
    },
    totalDistributed () {
      return this.visibleProductRows.reduce((sum, r) => sum + (r.allocated || 0), 0)
    },
    isDistributionBalanced () {
      return Math.abs(this.totalDistributed - Math.abs(this.delta)) < 0.001
    },
    remainingToDistribute () {
      return Math.abs(this.delta) - this.totalDistributed
    },
    totalShipmentNotes () {
      return this.productRows.reduce((sum, r) => sum + (r.shipmentCount || 0), 0)
    },
    distributionHeaders () {
      const ingredientLabel = this.selectedIngredient ? this.selectedIngredient.name : 'Sastojak'
      return [
        { text: 'Naziv artikla', value: 'productName', sortable: false },
        { text: 'Računi (aloc./ukup.)', value: 'receiptRatio', sortable: false, align: 'right' },
        { text: 'Ukupno prod.', value: 'totalQty', sortable: false, align: 'right' },
        { text: `${ingredientLabel} ukupno`, value: 'ingredientTotal', sortable: false, align: 'right' },
        { text: 'Ispravak (kg)', value: 'allocated', sortable: false, align: 'right' },
        { text: '', value: 'actions', sortable: false, width: 50 }
      ]
    },
    totalAllocatedReceipts () {
      return this.productRows.reduce((sum, r) => sum + (r.allocatedReceipts?.length || 0), 0)
    },
    totalReceipts () {
      return this.productRows.reduce((sum, r) => sum + (r.receipts?.length || 0), 0)
    },
    visibleProductRows () {
      if (this.distributionMode === 'proportional') {
        return this.productRows.filter(r => !r.isManual)
      }
      return this.productRows
    }
  },
  beforeDestroy () {
    this.clearPoll()
  },
  watch: {
    distributionMode (val) {
      if (val === 'proportional') this.recalcProportional()
    },
    manualSearch (val) {
      if (!val || val.length < 1) {
        this.manualProductOptions = []
        return
      }
      const search = val.toLowerCase()
      const existingIds = new Set(this.productRows.map(r => r.productId))
      // Filtriraj samo iz prodanih artikala u periodu
      this.manualProductOptions = this.soldProducts
        .filter(p => p.name.toLowerCase().includes(search) && !existingIds.has(p.id))
        .slice(0, 15)
    }
  },
  methods: {
    formatNum (n) {
      if (n == null) return '—'
      return Number(n).toFixed(3)
    },

    calcPerUnit (item) {
      const soldQty = item.totalQty / 1000
      if (!soldQty || soldQty === 0) return '—'
      // allocated je u kg, množimo s 1000 za mili-jedinice (grame)
      const perUnit = (item.allocated * 1000) / soldQty
      return perUnit.toFixed(3)
    },

    addManualProduct (product) {
      if (!product) return
      this.productRows.push({
        productId: product.id,
        productName: product.name,
        normQtyPerUnit: 0,
        totalQty: product.totalQty || 0,
        shipmentCount: product.receiptCount || 0,
        receipts: product.receiptIds || [], // { id, date } objects
        ingredientTotal: 0,
        allocated: 0,
        allocatedReceipts: [],
        isManual: true
      })
      this.$nextTick(() => {
        this.manualProduct = null
        this.manualSearch = ''
        this.manualProductOptions = []
      })
    },

    clearRowAllocation (item) {
      item.allocated = 0
      item.allocatedReceipts = []
      item.excluded = true
      if (this.distributionMode === 'proportional') {
        this.recalcProportional()
      }
    },

    includeRow (item) {
      item.excluded = false
      if (this.distributionMode === 'proportional') {
        this.recalcProportional()
      }
    },

    clearAllAllocations () {
      this.productRows.forEach(r => {
        r.allocated = 0
        r.allocatedReceipts = []
        r.excluded = true
      })
    },

    async loadIngredients () {
      if (this.ingredients.length > 0) return
      this.loadingIngredients = true
      try {
        const snap = await df
          .collection(`companies/${this.companyId}/items`)
          .where('status', '==', 'OK')
          .get()
        this.ingredients = snap.docs
          .filter(d => !d.data().has_norm)
          .map(d => {
            const data = d.data()
            return {
              id: d.id,
              name: data.name || d.id,
              unit: data.selling_unit || data.unit || '',
              warehouse_units: data.warehouse_units || null
            }
          }).sort((a, b) => a.name.localeCompare(b.name))
      } catch (e) {
        console.warn('Failed to load ingredients', e)
      } finally {
        this.loadingIngredients = false
      }
    },

    async loadShipmentData () {
      this.loadingStep2 = true
      try {
        const ingredientId = this.selectedIngredient.id
        console.log('Selected ingredient:', ingredientId, this.selectedIngredient.name)

        // Dohvati warehouse_units sa item dokumenta
        let warehouseUnits = null

        const companyItemDoc = await df
          .collection(`companies/${this.companyId}/items`)
          .doc(ingredientId)
          .get()
        if (companyItemDoc.exists) {
          const data = companyItemDoc.data()
          warehouseUnits = data.warehouse_units
          console.log('Warehouse units:', warehouseUnits)
        }

        this.selectedIngredient.warehouse_units = warehouseUnits
        console.log('Final warehouse_units:', warehouseUnits)

        // Korak 1: Dohvati sve proizvode koji imaju odabrani sastojak u normativu
        const normSnap = await df
          .collection(`companies/${this.companyId}/items`)
          .where('has_norm', '==', true)
          .get()

        const eligibleProducts = {}
        for (const doc of normSnap.docs) {
          const data = doc.data()
          const components = data.norm?.components
          if (!components) continue

          const list = Array.isArray(components) ? components : Object.values(components)
          const match = list.find(c => c.item_id === ingredientId)

          if (match) {
            console.log('Norm component for', data.name, ':', JSON.stringify(match))

            // Dohvati faktor konverzije iz warehouse_units
            // conversionFactor = customUnit.storage_amount / baseUnit.storage_amount
            // npr. 7 / 1000 = 0.007 (7 mjera = 0.007 kg)
            let conversionFactor = 1
            const wUnits = this.selectedIngredient.warehouse_units
            console.log('Looking for custom_unit_id:', match.custom_unit_id)
            console.log('warehouse_units:', wUnits)

            if (match.custom_unit && match.custom_unit_id && wUnits?.units) {
              const customUnit = wUnits.units[match.custom_unit_id]
              const baseUnitId = wUnits.base_unit
              const baseUnit = baseUnitId ? wUnits.units[baseUnitId] : null

              console.log('Custom unit:', customUnit)
              console.log('Base unit:', baseUnit)

              if (customUnit && baseUnit) {
                const customStorage = customUnit.storage_amount || 1
                const baseStorage = baseUnit.storage_amount || 1000
                conversionFactor = customStorage / baseStorage
                console.log(`Conversion: ${customStorage} / ${baseStorage} = ${conversionFactor}`)
              } else if (customUnit) {
                // Ako nema base unit, koristi storage_amount / 1000
                conversionFactor = (customUnit.storage_amount || 1) / 1000
                console.log(`Conversion (no base): ${customUnit.storage_amount} / 1000 = ${conversionFactor}`)
              }
            }

            eligibleProducts[doc.id] = {
              name: data.name,
              normQty: match.qty || match.quantity || 0, // količina u mili-jedinicama
              conversionFactor, // faktor za pretvorbu u osnovnu jedinicu
              customUnitId: match.custom_unit_id || null
            }
          }
        }
        console.log('Eligible products (with ingredient in norm):', Object.keys(eligibleProducts).length)
        console.log('Eligible product IDs:', Object.keys(eligibleProducts))
        console.log('Eligible products detail:', eligibleProducts)

        if (Object.keys(eligibleProducts).length === 0) {
          this.productRows = []
          this.step = 2
          return
        }

        // Korak 2: Dohvati račune po datumu i lokaciji (kao ItemReport)
        const dateFrom = new Date(this.dateFrom).getTime() / 1000
        const dateTo = new Date(this.dateTo).getTime() / 1000 + 86399

        console.log('Query params:', {
          companyId: this.companyId,
          locationId: this.locationId,
          warehouseId: this.warehouseId,
          dateFrom,
          dateTo,
          dateFromHuman: new Date(dateFrom * 1000).toISOString(),
          dateToHuman: new Date(dateTo * 1000).toISOString()
        })

        let query = df
          .collection(`receipts/${this.companyId}/receipts`)
          .where('issue_date_time', '>=', dateFrom)
          .where('issue_date_time', '<=', dateTo)

        // Filter po lokaciji ako nije "all"
        if (this.locationId && this.locationId !== 'all') {
          query = query.where('location_details.id', '==', this.locationId)
        }

        console.log('Fetching receipts...')
        const receiptsSnap = await query.get()
        console.log('Total receipts:', receiptsSnap.docs.length)

        // Debug: prvi račun
        if (receiptsSnap.docs.length > 0) {
          const firstReceipt = receiptsSnap.docs[0].data()
          console.log('First receipt location_details:', firstReceipt.location_details)
          console.log('First receipt items count:', firstReceipt.items?.length)
          console.log('First receipt ALL item IDs:', firstReceipt.items?.map(i => i.id))
          console.log('First receipt items (first 3):', firstReceipt.items?.slice(0, 3).map(i => ({ id: i.id, name: i.name })))

          // Provjeri prvih 5 računa za sve item ID-jeve
          const allItemIds = new Set()
          receiptsSnap.docs.slice(0, 20).forEach(d => {
            d.data().items?.forEach(i => allItemIds.add(i.id))
          })
          console.log('Sample item IDs from first 20 receipts:', Array.from(allItemIds).slice(0, 15))
        }

        // Korak 3: Agregiraj količine po proizvodima (kao ItemReport)
        const productMap = {}
        const allSoldProducts = {} // svi prodani artikli u periodu
        let totalItemsProcessed = 0
        let matchedItems = 0

        for (const rDoc of receiptsSnap.docs) {
          const receipt = rDoc.data()
          const items = receipt.items || []
          const receiptDate = receipt.issue_date_time || 0 // Unix timestamp

          for (const item of items) {
            totalItemsProcessed++
            // Item ID format: productId.warehouseItemId - uzmi samo productId
            const productId = item.id.includes('.') ? item.id.split('.')[0] : item.id

            // Spremi sve prodane artikle (za ručno dodavanje)
            if (!allSoldProducts[productId]) {
              allSoldProducts[productId] = {
                id: productId,
                name: item.name || productId,
                totalQty: 0,
                receiptCount: 0,
                receiptIds: []
              }
            }
            const qty = item.amt || item.quantity || 0
            allSoldProducts[productId].totalQty += qty
            allSoldProducts[productId].receiptCount++
            if (!allSoldProducts[productId].receiptIds.find(r => r.id === rDoc.id)) {
              allSoldProducts[productId].receiptIds.push({ id: rDoc.id, date: receiptDate })
            }

            // Provjeri je li ovaj proizvod ima odabrani sastojak u normativu
            if (!eligibleProducts[productId]) continue
            matchedItems++

            if (!productMap[productId]) {
              productMap[productId] = {
                productId,
                productName: eligibleProducts[productId].name || item.name || productId,
                normQty: eligibleProducts[productId].normQty,
                conversionFactor: eligibleProducts[productId].conversionFactor,
                totalQty: 0,
                receiptCount: 0,
                receipts: [], // { id, date } objects
                ingredientTotal: 0,
                allocated: 0,
                allocatedReceipts: [] // receipts that get allocation
              }
            }
            productMap[productId].totalQty += qty
            productMap[productId].receiptCount++
            if (!productMap[productId].receipts.find(r => r.id === rDoc.id)) {
              productMap[productId].receipts.push({ id: rDoc.id, date: receiptDate })
            }
          }
        }

        // Spremi sve prodane artikle za dropdown
        this.soldProducts = Object.values(allSoldProducts)
        console.log('All sold products in period:', this.soldProducts.length)

        console.log('Total items processed:', totalItemsProcessed)
        console.log('Matched items (in eligible products):', matchedItems)

        // Izračunaj ingredientTotal za svaki proizvod
        // totalQty je u mili-jedinicama (1000 = 1 kom)
        // normQty je u mili-jedinicama (1000 = 1 jedinica custom mjere)
        // conversionFactor je koliko osnovne jedinice ima u 1 custom mjeri (npr. 0.007 kg)
        for (const row of Object.values(productMap)) {
          const actualQty = row.totalQty / 1000 // prodano u stvarnim jedinicama
          const normUnits = row.normQty / 1000 // koliko custom mjera po jedinici proizvoda
          row.ingredientTotal = actualQty * normUnits * row.conversionFactor
          row.shipmentCount = row.receiptCount
          console.log(`${row.productName}: ${actualQty} × ${normUnits} × ${row.conversionFactor} = ${row.ingredientTotal}, receipts: ${row.receipts.length}`)
        }

        console.log('Products found:', Object.keys(productMap).length)
        console.log('Product map:', productMap)
        this.productRows = Object.values(productMap)
        this.recalcProportional()
        this.step = 2
      } catch (e) {
        console.warn('Failed to load data', e)
      } finally {
        this.loadingStep2 = false
      }
    },

    async getNormQtyForIngredient (productId) {
      try {
        const doc = await df
          .collection('companies')
          .doc(this.companyId)
          .collection('items')
          .doc(productId)
          .get()
        if (!doc.exists) return null
        const data = doc.data()
        const components = data?.norm?.components
        if (!components) return null
        // components može biti array ili map (objekt)
        const list = Array.isArray(components) ? components : Object.values(components)
        const match = list.find(c => c.id === this.selectedIngredient.id || c.item_id === this.selectedIngredient.id)
        return match ? (match.qty || match.quantity || 0) : null
      } catch {
        return null
      }
    },

    recalcProportional () {
      // Samo ne-manualni i ne-excluded redovi sudjeluju
      const activeRows = this.productRows.filter(r => !r.excluded && !r.isManual)

      // Excluded i manual rows keep 0
      this.productRows.filter(r => r.excluded || r.isManual).forEach(r => {
        r.allocated = 0
        r.allocatedReceipts = []
      })

      if (activeRows.length === 0) {
        return
      }

      // Ukupni ingredientTotal za proporcionalni izračun
      const totalIngredient = activeRows.reduce((s, r) => s + (r.ingredientTotal || 0), 0)
      if (totalIngredient === 0) {
        activeRows.forEach(r => {
          r.allocated = 0
          r.allocatedReceipts = []
        })
        return
      }

      const absDelta = Math.abs(this.delta) // delta u kg
      const absDeltaMilli = absDelta * 1000 // delta u mili-jedinicama

      // Za svaki proizvod izračunaj proporcionalni udio i per-receipt količinu
      for (const row of activeRows) {
        const proportion = (row.ingredientTotal || 0) / totalIngredient
        const allocatedForProduct = absDelta * proportion // u kg
        const allocatedMilli = allocatedForProduct * 1000 // u mili-jedinicama

        const receipts = row.receipts || []
        const receiptCount = receipts.length

        if (receiptCount === 0) {
          row.allocated = 0
          row.allocatedReceipts = []
          continue
        }

        // Per-receipt količina u mili-jedinicama
        const perReceiptMilli = allocatedMilli / receiptCount

        if (perReceiptMilli >= 1) {
          // Svi računi dobivaju alokaciju
          row.allocatedReceipts = receipts.map(r => r.id)
          row.allocated = allocatedForProduct
        } else {
          // Per-receipt je manje od 1 mili-jedinice
          // Smanji broj računa tako da svaki dobije minimalno 1 mili-jedinicu
          const maxReceipts = Math.floor(allocatedMilli)
          // Sortiraj po datumu (najstariji prvo) i uzmi maxReceipts
          const sortedReceipts = [...receipts].sort((a, b) => a.date - b.date)
          const selectedReceipts = sortedReceipts.slice(0, maxReceipts)
          row.allocatedReceipts = selectedReceipts.map(r => r.id)
          // Svaki odabrani račun dobiva 1 mili-jedinicu
          row.allocated = selectedReceipts.length / 1000 // u kg
        }
      }
    },

    async submitCorrection () {
      this.submitting = true
      this.jobError = null
      try {
        const user = state.getUser()
        const userName = user ? `${user.name || ''} ${user.surname || ''}`.trim() : ''
        const payload = {
          warehouse_id: this.warehouseId,
          ingredient_id: this.selectedIngredient.id,
          ingredient_name: this.selectedIngredient.name,
          date_from: new Date(this.dateFrom).toISOString(),
          date_to: new Date(this.dateTo + 'T23:59:59').toISOString(),
          delta: Math.round(this.delta * 1000), // u mili-jedinicama (int)
          distribution: this.productRows
            .filter(r => r.allocated > 0 && r.allocatedReceipts && r.allocatedReceipts.length > 0)
            .map(r => {
              const sign = this.delta >= 0 ? 1 : -1 // predznak iz delta
              const allocatedCount = r.allocatedReceipts.length
              const allocatedMilli = r.allocated * 1000 // allocated je u kg, pretvaramo u mili-jedinice
              const totalToAdd = Math.round(allocatedMilli) * sign // ukupno mili-jedinica s predznakom
              // per_unit = mili-jedinice po računu × 1000 (za preciznost na backendu)
              const perUnitMilli = allocatedMilli / allocatedCount
              const perUnit = Math.round(perUnitMilli * 1000) * sign
              return {
                product_id: r.productId,
                product_name: r.productName,
                total_sold: allocatedCount,
                total_quantity_to_add: totalToAdd,
                per_unit: perUnit,
                receipt_ids: r.allocatedReceipts
              }
            }),
          created_by: userName
        }

        const apiUrl = process.env.VUE_APP_NORMCORRECTION_API_URL
        const res = await axios.post(`${apiUrl}/init`, payload)
        this.jobId = res.data?.job_id || res.data?.id || null
        if (this.jobId) {
          this.startPolling()
        } else {
          this.jobDone = true
        }
      } catch (e) {
        this.jobError = e?.response?.data?.message || e.message || 'Greška pri slanju'
      } finally {
        this.submitting = false
      }
    },

    startPolling () {
      this.jobProgress = 0
      this.jobStatus = 'U tijeku...'
      this.pollInterval = setInterval(async () => {
        try {
          const doc = await df
            .collection('normative_corrections')
            .doc(this.warehouseId)
            .collection('corrections')
            .doc(this.jobId)
            .get()
          if (!doc.exists) return
          const data = doc.data()
          const status = data.status || ''
          const processed = data.processed_items || 0
          const total = data.total_items || 1
          this.jobProgress = Math.round((processed / total) * 100)
          this.jobStatus = status
          if (status === 'completed' || status === 'failed') {
            this.clearPoll()
            this.jobDone = status === 'completed'
            if (status === 'failed') this.jobError = data.error || 'Ispravak nije uspio'
          }
        } catch (e) { /* ignore */ }
      }, 3000)
    },

    clearPoll () {
      if (this.pollInterval) {
        clearInterval(this.pollInterval)
        this.pollInterval = null
      }
    },

    resetWizard () {
      this.clearPoll()
      Object.assign(this.$data, this.$options.data.call(this))
    }
  }
}
</script>

<style scoped>
.glass-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 179, 190, 0.15) !important;
  overflow: hidden;
}

.gradient-header {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.9) 0%,
    rgba(57, 137, 201, 0.85) 50%,
    rgba(97, 84, 164, 0.9) 100%);
}

.glass-stepper {
  background: transparent !important;
  box-shadow: none !important;
}

.glass-input >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 8px !important;
}

.glass-table {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.04) 0%,
    rgba(57, 137, 201, 0.03) 50%,
    rgba(97, 84, 164, 0.04) 100%) !important;
  border: 1px solid rgba(0, 179, 190, 0.15) !important;
  border-radius: 8px !important;
}

.glass-toggle {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  border-radius: 8px !important;
}

.gradient-btn {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.95) 0%,
    rgba(57, 137, 201, 0.9) 50%,
    rgba(97, 84, 164, 0.95) 100%) !important;
  color: white !important;
  border-radius: 10px !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 4px 12px rgba(0, 179, 190, 0.3) !important;
}

.gradient-btn:hover {
  box-shadow: 0 6px 16px rgba(0, 179, 190, 0.4) !important;
  transform: translateY(-1px);
}

.totals-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  backdrop-filter: blur(10px);
}

.totals-ok {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.12) 0%, rgba(56, 142, 60, 0.08) 100%);
  border: 1px solid rgba(76, 175, 80, 0.4);
  color: #2e7d32;
}

.totals-warn {
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.12) 0%, rgba(255, 193, 7, 0.08) 100%);
  border: 1px solid rgba(255, 152, 0, 0.4);
  color: #e65100;
}

.summary-box {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.06) 0%,
    rgba(57, 137, 201, 0.05) 50%,
    rgba(97, 84, 164, 0.06) 100%);
  border: 1px solid rgba(0, 179, 190, 0.18);
  border-radius: 10px;
  padding: 16px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 179, 190, 0.08);
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-label {
  color: rgba(0, 0, 0, 0.55);
  font-size: 13px;
}

.summary-value {
  font-weight: 500;
  font-size: 13px;
}

.gradient-progress >>> .v-progress-linear__determinate {
  background: linear-gradient(90deg,
    rgba(0, 179, 190, 1) 0%,
    rgba(57, 137, 201, 1) 50%,
    rgba(97, 84, 164, 1) 100%) !important;
}
</style>
